import { useState } from 'react';
import { TextInput } from '@c2fo/liquidity';
import { FormField } from '../FormField';
import type { RetryConfig } from '../../api/shared';

interface RetryConfigEditorProps {
  retryConfig?: RetryConfig;
  onChange: (config: RetryConfig | undefined) => void;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  initialDelay: '1s',
  maxDelay: '30s',
  backoffMultiplier: 2,
};

export const RetryConfigEditor: React.FC<RetryConfigEditorProps> = ({ retryConfig, onChange }) => {
  const [enabled, setEnabled] = useState(!!retryConfig);

  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked);
    if (checked) {
      onChange(retryConfig ?? DEFAULT_RETRY_CONFIG);
    } else {
      onChange(undefined);
    }
  };

  const handleMaxAttemptsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1) {
      onChange({
        ...retryConfig,
        maxAttempts: value,
        initialDelay: retryConfig?.initialDelay ?? DEFAULT_RETRY_CONFIG.initialDelay,
        maxDelay: retryConfig?.maxDelay ?? DEFAULT_RETRY_CONFIG.maxDelay,
        backoffMultiplier: retryConfig?.backoffMultiplier ?? DEFAULT_RETRY_CONFIG.backoffMultiplier,
      });
    }
  };

  const handleInitialDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...retryConfig,
      maxAttempts: retryConfig?.maxAttempts ?? DEFAULT_RETRY_CONFIG.maxAttempts,
      initialDelay: e.target.value,
    });
  };

  const handleMaxDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...retryConfig,
      maxAttempts: retryConfig?.maxAttempts ?? DEFAULT_RETRY_CONFIG.maxAttempts,
      maxDelay: e.target.value,
    });
  };

  const handleBackoffMultiplierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 1) {
      onChange({
        ...retryConfig,
        maxAttempts: retryConfig?.maxAttempts ?? DEFAULT_RETRY_CONFIG.maxAttempts,
        backoffMultiplier: value,
      });
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Retry Configuration</h3>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="enableRetry"
          checked={enabled}
          onChange={(e) => handleEnabledChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="enableRetry" className="ml-2 text-sm text-gray-700">
          Enable retry on failure
        </label>
      </div>

      {enabled && (
        <div className="grid grid-cols-2 gap-4 pl-6">
          <FormField label="Max Attempts">
            <TextInput
              type="number"
              min="1"
              max="10"
              value={retryConfig?.maxAttempts ?? DEFAULT_RETRY_CONFIG.maxAttempts}
              onChange={handleMaxAttemptsChange}
              className="w-full"
              size="sm"
            />
            <p className="text-xs text-gray-500 mt-1">Number of retry attempts (1-10)</p>
          </FormField>

          <FormField label="Initial Delay">
            <TextInput
              value={retryConfig?.initialDelay ?? DEFAULT_RETRY_CONFIG.initialDelay}
              onChange={handleInitialDelayChange}
              placeholder="1s"
              className="w-full font-mono"
              size="sm"
            />
            <p className="text-xs text-gray-500 mt-1">e.g., 1s, 500ms, 1m</p>
          </FormField>

          <FormField label="Max Delay">
            <TextInput
              value={retryConfig?.maxDelay ?? DEFAULT_RETRY_CONFIG.maxDelay}
              onChange={handleMaxDelayChange}
              placeholder="30s"
              className="w-full font-mono"
              size="sm"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum delay between retries</p>
          </FormField>

          <FormField label="Backoff Multiplier">
            <TextInput
              type="number"
              min="1"
              step="0.5"
              value={retryConfig?.backoffMultiplier ?? DEFAULT_RETRY_CONFIG.backoffMultiplier}
              onChange={handleBackoffMultiplierChange}
              className="w-full"
              size="sm"
            />
            <p className="text-xs text-gray-500 mt-1">Exponential backoff factor</p>
          </FormField>
        </div>
      )}
    </div>
  );
};

export const RetryConfigDisplay: React.FC<{ retryConfig?: RetryConfig }> = ({ retryConfig }) => {
  if (!retryConfig) {
    return <p className="text-sm text-gray-500 italic">No retry configuration</p>;
  }

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
      <div>
        <p className="text-xs text-gray-500">Max Attempts</p>
        <p className="text-sm text-gray-900">{retryConfig.maxAttempts}</p>
      </div>
      {retryConfig.initialDelay && (
        <div>
          <p className="text-xs text-gray-500">Initial Delay</p>
          <p className="text-sm text-gray-900 font-mono">{retryConfig.initialDelay}</p>
        </div>
      )}
      {retryConfig.maxDelay && (
        <div>
          <p className="text-xs text-gray-500">Max Delay</p>
          <p className="text-sm text-gray-900 font-mono">{retryConfig.maxDelay}</p>
        </div>
      )}
      {retryConfig.backoffMultiplier && (
        <div>
          <p className="text-xs text-gray-500">Backoff Multiplier</p>
          <p className="text-sm text-gray-900">{retryConfig.backoffMultiplier}x</p>
        </div>
      )}
    </div>
  );
};
